import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { externalSubscriptionService } from '@/services/externalSubscriptionService';
import { fetchStripeAccountId } from '@/services/transactions/stripeAccountService';

interface UseSubscriptionDataProps {
  stripeConnected: boolean;
}

export interface SubscriptionRecord {
  id: string;
  stripe_subscription_id: string;
  stripe_price_id: string;
  status: string;
  created_at: string;
  current_period_end?: string;
  current_period_start?: string;
  customer_email?: string;
  customer_name?: string;
  product_name?: string;
  billing_interval?: string;
  metadata?: Record<string, any>;
  amount?: number;
  created?: number;
  has_pending_upgrade?: boolean;
  upgrade_status?: string;
  upgrade_id?: string;
  grace_period_end?: string;
  new_price_id?: string;
  payment_method_updated_at?: string;
}

// Module-level cache to persist data across component unmounts/remounts.
// This prevents re-fetching when switching tabs — data stays in memory
// without needing a React Context (which causes auth race conditions).
let moduleCache: {
  subscriptions: SubscriptionRecord[];
  rawStripeData: any[];
  products: any[];
  hasFetched: boolean;
} = {
  subscriptions: [],
  rawStripeData: [],
  products: [],
  hasFetched: false,
};

export interface SubscriptionFilters {
  status: string;
  product: string;
  billingPeriod: string;
}

export const useSubscriptionData = ({ stripeConnected }: UseSubscriptionDataProps) => {
  // Initialize state from module cache if available
  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>(moduleCache.subscriptions);
  const [rawStripeData, setRawStripeData] = useState<any[]>(moduleCache.rawStripeData);
  const [loading, setLoading] = useState(!moduleCache.hasFetched); // Not loading if already fetched
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<any[]>(moduleCache.products);

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [billingPeriodFilter, setBillingPeriodFilter] = useState<string>('all');
  const [isFilterActive, setIsFilterActive] = useState(false);

  // Ref to prevent concurrent fetches
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const activeFilters = [
      statusFilter !== 'all',
      productFilter !== 'all',
      billingPeriodFilter !== 'all'
    ].filter(Boolean).length;
    
    setIsFilterActive(activeFilters > 0);
  }, [statusFilter, productFilter, billingPeriodFilter]);

  const loadAllSubscriptions = useCallback(async (forceRefresh = false) => {
    // Skip if already fetched and not forcing refresh (check module-level cache)
    if (!forceRefresh && moduleCache.hasFetched) {
      return;
    }

    // Skip if already fetching
    if (isFetchingRef.current) {
      return;
    }

    if (!stripeConnected) {
      setLoading(false);
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      console.log('[SubscriptionData] Fetching ALL subscriptions from Stripe...', forceRefresh ? '(forced refresh)' : '(initial load)');
      
      const connectedAccountId = await fetchStripeAccountId();
      
      if (!connectedAccountId) {
        throw new Error('No Stripe connected account found. Please connect your Stripe account first.');
      }

      const data = await externalSubscriptionService.fetchAllSubscriptions({
        connectedAccountId,
        limit: 100,
        statusFilter: 'all'
      });

      if (!data) {
        throw new Error('No data received from edge function');
      }

      const allSubscriptions = data.subscriptions || [];
      setRawStripeData(allSubscriptions);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      let upgradeRecords: any[] = [];
      if (user) {
        const { data: upgrades } = await supabase
          .from('subscription_upgrades')
          .select('*')
          .eq('user_id', user.id);
        
        upgradeRecords = upgrades || [];
      }
      
      // Combine Stripe subscriptions with Supabase upgrade records
      const mappedSubscriptions: SubscriptionRecord[] = allSubscriptions.map((stripeSub: any) => {
        const items = Array.isArray(stripeSub.items) ? stripeSub.items : stripeSub.items?.data || [];
        const primaryItem = items[0];
        
        const upgradeRecord = upgradeRecords.find(
          upgrade => upgrade.stripe_subscription_id === stripeSub.id
        );
        
        const customerName = stripeSub.customer?.name || stripeSub.customerName || '';
        const customerEmail = stripeSub.customerEmail || stripeSub.customer?.email || '';
        const billingInterval = primaryItem?.price?.recurring?.interval || '';
        const productName = primaryItem?.product?.name || 'Unknown Product';
        
        const baseSubscription: SubscriptionRecord = {
          id: stripeSub.id,
          stripe_subscription_id: stripeSub.id,
          stripe_price_id: primaryItem?.price?.id || '',
          status: stripeSub.status,
          created_at: new Date(stripeSub.created * 1000).toISOString(),
          current_period_end: stripeSub.current_period_end ? new Date(stripeSub.current_period_end * 1000).toISOString() : undefined,
          current_period_start: stripeSub.current_period_start ? new Date(stripeSub.current_period_start * 1000).toISOString() : undefined,
          customer_email: customerEmail,
          customer_name: customerName,
          product_name: productName, 
          billing_interval: billingInterval,
          metadata: stripeSub.metadata || {},
          amount: primaryItem?.price?.unit_amount || 0,
          created: stripeSub.created || 0
        };
        
        if (upgradeRecord) {
          baseSubscription.has_pending_upgrade = true;
          baseSubscription.upgrade_status = upgradeRecord.status;
          baseSubscription.upgrade_id = upgradeRecord.id;
          baseSubscription.grace_period_end = upgradeRecord.grace_period_end;
          baseSubscription.new_price_id = upgradeRecord.new_price_id;
          baseSubscription.payment_method_updated_at = upgradeRecord.payment_method_updated_at;
        } else {
          baseSubscription.has_pending_upgrade = false;
        }
        
        return baseSubscription;
      });

      // Extract unique products from Stripe subscriptions for filtering
      const uniqueProducts = new Map();
      allSubscriptions.forEach((stripeSub: any) => {
        const items = Array.isArray(stripeSub.items) ? stripeSub.items : stripeSub.items?.data || [];
        items.forEach((item: any) => {
          const product = item.product;
          if (product && product.id && !uniqueProducts.has(product.id)) {
            uniqueProducts.set(product.id, {
              id: product.id,
              name: product.name || 'Unknown Product',
              billing_period: item.price?.recurring?.interval || 'one-time'
            });
          }
        });
      });

      const productsArray = Array.from(uniqueProducts.values());

      // Update local state
      setSubscriptions(mappedSubscriptions);
      setProducts(productsArray);
      
      // Update module-level cache so data persists across unmounts
      moduleCache = {
        subscriptions: mappedSubscriptions,
        rawStripeData: allSubscriptions,
        products: productsArray,
        hasFetched: true
      };
      
    } catch (e: any) {
      console.error('[SubscriptionData] Error combining Stripe + Supabase data:', e);
      setError(e.message);
      setSubscriptions([]);
      setProducts([]);
      setRawStripeData([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [stripeConnected]);

  // Initial load — only runs once when stripeConnected is true
  useEffect(() => {
    if (stripeConnected && !moduleCache.hasFetched) {
      loadAllSubscriptions(false);
    } else if (!stripeConnected) {
      setLoading(false);
    }
  }, [stripeConnected, loadAllSubscriptions]);

  useEffect(() => {
    const handleForceRefresh = () => loadAllSubscriptions(true);
    document.addEventListener('subscription-force-refresh', handleForceRefresh);
    return () => document.removeEventListener('subscription-force-refresh', handleForceRefresh);
  }, [loadAllSubscriptions]);

  // Function to manually refresh subscriptions (called by refresh button)
  const refreshSubscriptions = useCallback(() => {
    return loadAllSubscriptions(true);
  }, [loadAllSubscriptions]);

  const resetFilters = () => {
    setStatusFilter('all');
    setProductFilter('all');
    setBillingPeriodFilter('all');
  };

  const getProduct = (id: string) => products.find((p: any) => p.id === id) || {};

  const getFilteredSubscriptions = () => {
    let filtered = subscriptions;
    if (statusFilter !== 'all') filtered = filtered.filter(s => s.status === statusFilter);
    if (productFilter !== 'all') filtered = filtered.filter(s => s.stripe_price_id === productFilter);
    if (billingPeriodFilter !== 'all') {
      filtered = filtered.filter(s => (getProduct(s.stripe_price_id || '') as any)?.billing_period === billingPeriodFilter);
    }
    return filtered;
  };

  return {
    subscriptions: getFilteredSubscriptions(),
    rawStripeData,
    loading,
    error,
    products,
    filters: {
      status: statusFilter,
      product: productFilter,
      billingPeriod: billingPeriodFilter
    },
    isFilterActive,
    setStatusFilter,
    setProductFilter,
    setBillingPeriodFilter,
    resetFilters,
    getProduct,
    refreshSubscriptions
  };
};
